import React from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from 'recharts';
import { safeMetricsData } from './util';

function MetricsPanel({ plotType, setPlotType, metricsData, metricsLoading }) {
  // Determine if there's any data for the selected plot
  const hasData = () => {
    if (!metricsData) return false;
    if (plotType === 'confusion') return metricsData.matrix?.length > 0; //check for matrix data
    return safeMetricsData(metricsData, plotType)?.length > 0;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant='h6'>Model Metrics</Typography>

      {/* Plot Type Selector */}
      <FormControl size='small' sx={{ mt: 2, mb: 2 }}>
        <InputLabel>Plot Type</InputLabel>
        <Select
          value={plotType}
          label='Plot Type'
          onChange={(e) => setPlotType(e.target.value)}
        >
          <MenuItem value='roc'>ROC</MenuItem>
          <MenuItem value='confusion'>Confusion Matrix</MenuItem>
          <MenuItem value='precision-recall'>Precision Recall</MenuItem>
          <MenuItem value='prediction-distribution'>
            Prediction Distribution
          </MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ minHeight: 350 }}>
        {metricsLoading ? (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            height={300}
          >
            <CircularProgress />
          </Box>
        ) : !hasData() ? (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            height={300}
          >
            <Typography variant='body2' color='text.secondary'>
              No Plot Data Available
            </Typography>
          </Box>
        ) : (
          <>
            {/* ROC Curve */}
            {plotType === 'roc' && (
              <ResponsiveContainer width='100%' height={300}>
                <LineChart data={safeMetricsData(metricsData, 'roc')}>
                  <CartesianGrid strokeDasharray='3 3' />

                  <XAxis
                    dataKey='fpr'
                    type='number'
                    domain={[0, 1]}
                    label={{
                      value: 'False Positive Rate',
                      position: 'insideBottom',
                      offset: -5,
                    }}
                  />

                  <YAxis
                    type='number'
                    domain={[0, 1]}
                    label={{
                      value: 'True Positive Rate',
                      angle: -90,
                      position: 'insideLeft',
                    }}
                  />

                  <Tooltip />

                  {/* Random classifier */}
                  <Line
                    type='linear'
                    data={[
                      { fpr: 0, tpr: 0 },
                      { fpr: 1, tpr: 1 },
                    ]}
                    dataKey='tpr'
                    stroke='#999'
                    strokeDasharray='5 5'
                    dot={false}
                    isAnimationActive={false}
                  />

                  {/* ROC curve */}
                  <Line
                    type='linear'
                    dataKey='tpr'
                    stroke='#ff5722'
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}

            {/* Precision-Recall Curve */}
            {plotType === 'precision-recall' && (
              <ResponsiveContainer width='100%' height={300}>
                <LineChart
                  data={safeMetricsData(metricsData, 'precision-recall')}
                >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='recall' />
                  <YAxis dataKey='precision' domain={[0, 1]} />
                  <Tooltip />
                  <Line type='monotone' dataKey='precision' stroke='#4caf50' />
                </LineChart>
              </ResponsiveContainer>
            )}

            {/* Prediction Distribution */}
            {plotType === 'prediction-distribution' && (
              <ResponsiveContainer width='100%' height={300}>
                <BarChart
                  data={safeMetricsData(metricsData, 'prediction-distribution')}
                >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='bucket' />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey='count' fill='#2196f3' />
                </BarChart>
              </ResponsiveContainer>
            )}

            {/* Confusion Matrix */}
            {plotType === 'confusion' && (
              <Box sx={{ width: '100%', height: 400, overflow: 'auto' }}>
                <Typography variant='h6' mb={2}>
                  Confusion Matrix
                </Typography>
                <TableContainer component={Paper} sx={{ height: '100%' }}>
                  <Table
                    size='small'
                    aria-label='confusion matrix'
                    sx={{ tableLayout: 'fixed' }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        {metricsData.labels.map((lbl, idx) => (
                          <TableCell
                            key={idx}
                            align='center'
                            sx={{ fontWeight: 'bold' }}
                          >
                            <Tooltip title={`Predicted label: ${lbl}`} arrow>
                              <span>Predicted: {lbl}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {metricsData.matrix.map((row, i) => {
                        const maxValue = Math.max(...metricsData.matrix.flat());
                        return (
                          <TableRow key={i}>
                            <TableCell
                              component='th'
                              scope='row'
                              sx={{ fontWeight: 'bold' }}
                            >
                              <Tooltip
                                title={`Actual label: ${metricsData.labels[i]}`}
                                arrow
                              >
                                <span>Actual: {metricsData.labels[i]}</span>
                              </Tooltip>
                            </TableCell>
                            {row.map((value, j) => {
                              const intensity = value / maxValue;
                              const bgColor = `rgba(33,150,243,${
                                intensity * 0.7 + 0.3
                              })`;
                              const color = intensity > 0.5 ? '#fff' : '#000';
                              return (
                                <TableCell
                                  key={j}
                                  align='center'
                                  sx={{
                                    backgroundColor: bgColor,
                                    color,
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}

export default MetricsPanel;
